import { useTranslation } from 'react-i18next';
import { useAdminImage } from '@/hooks/useAdminImage';

export default function BookingCTA() {
  const { t } = useTranslation();

  const ctaBg = useAdminImage(
    'home',
    'images.home.cta_bg',
    'https://public.readdy.ai/ai/img_res/edited_1f7e46d18c7b93468418a6f9e805f52a_04fc9dc7.jpg'
  );

  return (
    <section id="booking" className="flex flex-col lg:flex-row">
      <div className="relative flex-1 h-64 sm:h-80 md:h-96 lg:min-h-[600px] overflow-hidden">
        <img
          src={ctaBg}
          alt={t('home.booking_reception_alt')}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
        <div className="absolute bottom-6 md:bottom-8 left-6 md:left-8 right-6 md:right-8">
          <span className="text-xs tracking-[0.2em] uppercase text-white/60 font-label">
            {t('booking.tag')}
          </span>
          <h2 className="font-heading text-2xl md:text-4xl lg:text-6xl font-bold text-white mt-2 md:mt-3 leading-[1.05]">
            {t('booking.heading_line1')}
            <br />
            {t('booking.heading_line2')}
          </h2>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center bg-background-50 px-6 md:px-8 py-10 md:py-16 lg:px-16 lg:py-20">
        <div className="max-w-md w-full">
          <h2 className="font-heading text-xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight">
            {t('booking.heading_line1')}<br />{t('booking.heading_line2')}
          </h2>
          <p className="mt-3 md:mt-4 text-sm text-foreground-500 leading-relaxed font-body">
            {t('booking.description')}
          </p>

          <form
            data-readdy-form
            id="booking-form"
            action="https://readdy.ai/api/form/d8hes3rfkflngh85ccig"
            method="POST"
            className="mt-8 space-y-4"
          >
            <div>
              <label htmlFor="booking-name" className="block text-xs font-label font-medium text-foreground-600 mb-1.5">
                {t('booking.form_name_label')}
              </label>
              <input
                type="text"
                id="booking-name"
                name="name"
                required
                className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-100 text-sm text-foreground-800 font-body placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                placeholder={t('booking.form_name_placeholder')}
              />
            </div>

            <div>
              <label htmlFor="booking-email" className="block text-xs font-label font-medium text-foreground-600 mb-1.5">
                {t('imprint.email_label')}
              </label>
              <input
                type="email"
                id="booking-email"
                name="email"
                required
                className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-100 text-sm text-foreground-800 font-body placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                placeholder="ihre@email.de"
              />
            </div>

            <div>
              <label htmlFor="booking-phone" className="block text-xs font-label font-medium text-foreground-600 mb-1.5">
                {t('booking.form_phone_short')}
              </label>
              <input
                type="tel"
                id="booking-phone"
                name="phone"
                required
                className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-100 text-sm text-foreground-800 font-body placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                placeholder="+49 123 4567890"
              />
            </div>

            <div>
              <label htmlFor="booking-service" className="block text-xs font-label font-medium text-foreground-600 mb-1.5">
                {t('booking.form_treatment_label')}
              </label>
              <select
                id="booking-service"
                name="service"
                required
                className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-100 text-sm text-foreground-800 font-body focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all appearance-none cursor-pointer"
              >
                <option value="">{t('booking.form_select_please')}</option>
                <option value="dental">{t('services.dental.title')}</option>
                <option value="surgery">{t('services.surgery.title')}</option>
                <option value="consultation">{t('booking.service_consultation')}</option>
              </select>
            </div>

            <div>
              <label htmlFor="booking-message" className="block text-xs font-label font-medium text-foreground-600 mb-1.5">
                {t('booking.form_message_optional')}
              </label>
              <textarea
                id="booking-message"
                name="message"
                rows={3}
                maxLength={500}
                className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-100 text-sm text-foreground-800 font-body placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all resize-none"
                placeholder={t('booking.form_message_short_placeholder')}
              ></textarea>
            </div>

            <button
              type="submit"
              className="w-full py-3.5 rounded-full bg-primary-800 text-white text-sm tracking-[0.12em] uppercase font-label font-semibold hover:bg-primary-700 transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              {t('booking.cta')}
              <i className="ri-arrow-right-line text-lg"></i>
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}